//import styled from '@emotion/styled';
import { Canvg, presets } from 'canvg';
import { debounce } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import {
	BsChevronRight,
	BsPlusLg,
	BsFolderFill,
	BsArrowLeftShort,
} from 'react-icons/bs';
import Masonry from 'react-masonry-css';

import BayBreadcrumbs from '../../../BayBreadcrumbs';
// @ts-ignore - BayFilesProgressModal is JSX without TypeScript definitions
import BayFilesProgressModal from '../../../BayFilesProgressModal';
import BayTooltip from '../../../BayTooltip';
import BayWidgetLoadingPlaceholder from '../../../BayWidgetLoadingPlaceholder';

// TODO: move brand colors to color picker
// https://polotno.com/docs/config/#change-default-preset-colors-in-color-picker

/* const CustomInput = styled.div`
	position: relative;
	> svg {
		color: var(--bs-gray-500);
		position: absolute;
		top: 12px;
		left: 12px;
	}
	> input {
		padding-left: 32px;
	}
`;

/* const SearchInput = ({ onChange, value, placeholder }) => {
	return (
		<CustomInput className="position-relative">
			<BsSearch size={16} />
			<Form.Control
				placeholder={placeholder}
				type="text"
				value={value}
				onChange={onChange}
			/>
		</CustomInput>
	);
}; */

interface BreadcrumbItem {
	id: number;
	label: string;
	path: string;
}

interface BrandAssetEntry {
	_id: string;
	_type: string;
	name: string;
	isHidden?: boolean;
	cloudObject?: {
		objKey: string;
	};
	url?: string;
}

interface BrandAssetsResponse {
	result: BrandAssetEntry[];
}

const initialBreadCrumbArray: BreadcrumbItem[] = [
	{
		id: 0,
		label: 'My Brand Assets',
		path: '/brand-assets',
	},
];

async function getSvgColors(element: any, urlToString: any, getColors: any): Promise<string[]> {
	const svgString = await urlToString(element);
	const colors = getColors(svgString);
	return colors;
}

async function toPng(src: string, width: number, height: number, preset: any): Promise<string> {
	const canvas = new OffscreenCanvas(width, height);
	const ctx = canvas.getContext('2d');

	const canvasObject = await Canvg.from(ctx, src, preset);
	canvasObject.resize(width, height, 'xMidYMid meet');
	await canvasObject.render();

	const blob = await canvas.convertToBlob();

	const dataUrl = await new Promise<string>((resolve) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.readAsDataURL(blob);
	});

	return dataUrl;
}

export const escapeRegExp = (val: any): string => {
	const reRegExpChar = /[\\^$.*+?()[{}|]/g;

	const str = val?.toString?.() ?? val;

	if (str === undefined) {
		throw new TypeError(`${val} can't be stringified`);
	}

	return RegExp(reRegExpChar.source).test(str)
		? str.replace(reRegExpChar, '[$&]')
		: str;
};

interface BrandPanelProps {
	store: any;
	t: (key: string) => string;
	api: any;
	actingAsState: any;
	getImageSize: (src: string) => Promise<{ width: number; height: number }>;
	urlToString: any;
	getColors: any;
	fileStoreApiUrl: string;
	showBrandAssets?: boolean;
	isDesignAdmin?: boolean;
}

const BrandPanel: React.FC<BrandPanelProps> = observer(
	({
		store,
		t,
		api,
		actingAsState,
		getImageSize,
		urlToString,
		getColors,
		fileStoreApiUrl,
		showBrandAssets = false,
		isDesignAdmin,
	}) => {
		const [breadCrumbsArray, setBreadCrumbsArray] = useState<BreadcrumbItem[]>(
			initialBreadCrumbArray,
		);
		// const [brandSearchInput, setBrandSearchInput] = useState('');
		const [folderPathString, setFolderPathString] = useState('');
		const [brandAssets, setBrandAssets] = useState<BrandAssetsResponse | null>(null);
		const [assetFolders, setAssetFolders] = useState<BrandAssetEntry[]>([]);
		const [assetFiles, setAssetFiles] = useState<BrandAssetEntry[]>([]);
		const [uploadingFiles] = useState<any[]>([]);
		const [showUploadFileModal, setShowUploadFileModal] = useState(false);
		const [isNestedFolder, setIsNestedFolder] = useState(false);
		const [isLoading, setIsLoading] = useState(false);

		const readBrandAssets = (brandSearchInput?: string) => {
			setIsLoading(true);
			api.entries
				.read(
					'brand-assets',
					{ search: brandSearchInput, showHidden: false, folderPath: '' },
					null,
					null,
				)
				.then((response: BrandAssetsResponse) => {
					setBrandAssets(response);
					createBreadcrumbs();
					setIsLoading(false);
				})
				.catch((error: Error) => {
					console.error('Error reading Brand Assets entries:', error);
					setIsLoading(false);
				});
		};

		const debounceFetchBrandAssets = useMemo(
			() => debounce(readBrandAssets, 500),
			[],
		);

		useEffect(() => {
			showBrandAssets && debounceFetchBrandAssets();
		}, []);

		useEffect(() => {
			createBreadcrumbs();
		}, [folderPathString]);

		useEffect(() => {
			if (breadCrumbsArray.length > 1) {
				setIsNestedFolder(true);
			}
			if (breadCrumbsArray.length === 1) {
				setIsNestedFolder(false);
			}
		}, [breadCrumbsArray]);

		useEffect(() => {
			if (brandAssets) {
				const foldersArray: BrandAssetEntry[] = [];
				const filesArray: BrandAssetEntry[] = [];
				brandAssets.result.forEach((asset) => {
					// Defense in depth: Filter out hidden assets at UI level
					// Backend should already filter these, but this ensures UI never shows them
					// even if backend filtering fails or is bypassed
					if (asset.isHidden) {
						return;
					}

					if (asset._type === 'Entry$Folder') {
						foldersArray.push(asset);
					}
					if (asset._type === 'Entry$File') {
						filesArray.push(asset);
					}
				});
				setAssetFolders(foldersArray);
				setAssetFiles(filesArray);
			}
		}, [brandAssets]);

		const preset = presets?.offscreen();

		const brandColors = isDesignAdmin
			? [
				actingAsState?.brand.primaryColor,
				actingAsState?.brand.secondaryColor,
			]
			: [
				actingAsState?.brandPrimaryColor,
				actingAsState?.brandSecondaryColor,
			];

		const colorsArray = brandColors.reduce((acc: string[], color: string) => {
			if (color && !acc.includes(color)) {
				acc.push(color);
			}
			return acc;
		}, []);

		const colorsTitlesArray = [t('colorsPrimary'), t('colorsSecondary')];

		const addImageToPage = async (src: string) => {
			try {
				const { width, height } = await getImageSize(src);
				let urlP = `https://imageproxy.services.bkat.io/?url=${encodeURIComponent(src)}&w=${width}&h=${height}`;

				store.history.transaction(async () => {
					const x = store.width / 2 - width / 2;
					const y = store.height / 2 - height / 2;

					if (src && src.endsWith('.svg')) {
						await toPng(src, width, height, preset).then(async (pngUrl) => {
							urlP = pngUrl;
							console.log('pngUrl', pngUrl);
						});
					}

					const element = store.activePage?.addElement({
						type: 'image',
						width,
						height,
						x,
						y,
					});
					element.set({
						src: urlP,
					});
				});
				console.log('Image resize is done!', width, height);
				setIsLoading(false);
			} catch (e) {
				console.error('Error adding image to page!', src);
				setIsLoading(false);
			}
		};

		const handleImageSelect = async (src: string) => {
			const elements = store.selectedElementsIds.toJSON();

			if (elements.length === 0) {
				setIsLoading(true);
				addImageToPage(src);
			} else {
				elements?.forEach(async (elementId: string) => {
					setIsLoading(true);
					if (isDesignAdmin) {
						try {
							const element = store.getElementById(elementId);
							if (element && element.type === 'image' && !element.locked) {
								const max_width = element.width;
								const max_height = element.height;

								let urlP = null;

								// The src url is an svg, fix it using canvg...
								// if (src.search(/.svg$/gi) !== -1 && !src.search(/.png$/gi)) {
								if (src && src.endsWith('.svg')) {
									await toPng(src, max_width, max_height, preset).then(
										async (pngUrl) => {
											const { width, height } = await getImageSize(pngUrl);
											urlP = pngUrl;
											console.log(
												'Converting SVG to PNG is done!',
												width,
												height,
											);
										},
									);
								} else {
									console.log('Not an SVG, skipping conversion...');

									//TODO: Look into using this for cropping?
									//&cx=${element.cropX*width}&cy=${element.cropY*height}&cw=${element.cropWidth*width}&ch=${element.cropHeight*height}&precrop=1
									urlP = `https://imageproxy.services.bkat.io/?url=${encodeURIComponent(src)}&w=${max_width}&h=${max_height}&fit=contain`;
									const { width, height } = await getImageSize(urlP);

									console.log('Image resize is done!', width, height);
								}

								if (
									element.cropX ||
									element.cropY ||
									element.cropWidth ||
									element.cropHeight
								) {
									element.set({
										cropX: 0,
										cropY: 0,
										cropWidth: 1,
										cropHeight: 1,
									});
								}

								element.set({
									src: urlP ?? src,
								});

								setIsLoading(false);
								return;
							}

							addImageToPage(src);
						} catch (error) {
							console.error('Brand image replacement failed:\r\n', error);
							setIsLoading(false);
						}
					} else {
						const element = store.getElementById(elementId);

						if (element && element.type === 'image' && !element.locked) {
							element.set({ src });
							setIsLoading(false);
							return;
						}

						addImageToPage(src);
					}
				});
			}
		};

		const handleColorSelect = async (color: string) => {
			const elements = store.selectedElementsIds.toJSON();

			elements.forEach(async (elementId: string) => {
				const element = store.getElementById(elementId);

				// Text or figure object
				if (
					element &&
					(element.type === 'text' || element.type === 'figure') &&
					!element.locked
				) {
					element.set({ fill: color });
					// return;
				}

				// svg element
				if (element && element.type === 'svg' && !element.locked) {
					await getSvgColors(element.src, urlToString, getColors).then(
						(colorArray) => {
							element.replaceColor(colorArray[0], color);
						},
					); // get colors from svg and replace first color
				}
			});
		};

		/* const searchHandler = (element) => {
			setBrandSearchInput(element.target.value);
		}; */

		const handleBackClick = (data: BreadcrumbItem) => {
			handleBreadcrumbClick(data);
		};

		const handleBreadcrumbClick = async (data: BreadcrumbItem) => {
			setIsLoading(true);
			setFolderPathString(data.path === '/brand-assets' ? '' : data.path);
			api.entries
				.read(
					'brand-assets',
					{
						search: '',
						showHidden: false,
						folderPath: data.path === '/brand-assets' ? '' : data.path,
					},
					null,
					null,
				)
				.then((response: BrandAssetsResponse) => {
					setBrandAssets(response);
					setIsLoading(false);
				})
				.catch((error: Error) => {
					console.error('Error reading folder contents:', error);
					setIsLoading(false);
				});
		};

		const createBreadcrumbs = () => {
			const crumbs =
				folderPathString?.length > 0
					? folderPathString?.split('/').map((e, index) => {
						return {
							id: index + 1,
							label: e,
							path: `${folderPathString
								?.split('/')
								.slice(0, index + 1)
								.join('/')}`,
						};
					})
					: [];
			setBreadCrumbsArray([...initialBreadCrumbArray, ...crumbs]);
		};

		const handleRowClick = (data: BrandAssetEntry) => {
			setIsLoading(true);
			if (data._type === 'Entry$Folder') {
				api.entries
					.read(
						'brand-assets',
						{
							search: '',
							showHidden: false,
							folderPath: data.cloudObject?.objKey,
						},
						null,
						null,
					)
					.then((response: BrandAssetsResponse) => {
						setBrandAssets(response);
						setIsLoading(false);
					})
					.catch((error: Error) => {
						console.error('Error reading folder contents:', error);
						setIsLoading(false);
					});
				setFolderPathString(data.cloudObject?.objKey || '');
			}
		};

		return (
			<div className="d-flex h-100 flex-column overflow-auto pe-3">
				{!isNestedFolder && (
					<div className="p-1 mb-5">
						<div>
							{actingAsState && (
								<>
									<h4>{t('logos-light')}</h4>
									<Button
										className="mb-3 ms-0 p-2 w-100 light-logo-button"
										variant="link"
										onClick={() =>
											handleImageSelect(
												isDesignAdmin
													? actingAsState.brand.logoLight
													: actingAsState?.logoLightUrl,
											)
										}
									>
										<img
											alt="Logo Light"
											crossOrigin="anonymous"
											src={
												isDesignAdmin
													? actingAsState.brand.logoLight
													: actingAsState?.logoLightUrl
											}
											style={{ maxWidth: '150px', maxHeight: '150px' }}
										/>
									</Button>
								</>
							)}
							{actingAsState && (
								<>
									<h4>{t('logos-dark')}</h4>
									<Button
										className="mb-3 ms-0 p-2 w-100 dark-logo-button"
										variant="link"
										onClick={() =>
											handleImageSelect(
												isDesignAdmin
													? actingAsState.brand.logoDark
													: actingAsState?.logoDarkUrl,
											)
										}
									>
										<img
											alt="Logo Dark"
											crossOrigin="anonymous"
											src={
												isDesignAdmin
													? actingAsState.brand.logoDark
													: actingAsState?.logoDarkUrl
											}
											style={{ maxWidth: '150px', maxHeight: '150px' }}
										/>
									</Button>
								</>
							)}
						</div>
					</div>
				)}

				{!isNestedFolder && (
					<div className="p-1">
						<div className="d-flex flex-column align-items-start">
							{colorsArray.length > 0 ? (
								colorsArray.map((colorValue, i) => {
									return (
										<React.Fragment key={colorValue || i}>
											<h4>{colorsTitlesArray[i]}</h4>
											<Button
												className="mb-3 d-block p-0 ps-2"
												variant="link"
												onClick={() => handleColorSelect(colorValue)}
											>
												<div
													style={{
														backgroundColor: colorValue,
														width: '30px',
														height: '30px',
														borderRadius: '50%',
														border: '1px solid var(--bs-light)',
													}}
												/>
											</Button>
										</React.Fragment>
									);
								})
							) : (
								<div className="fs-7" style={{ height: '30px' }}>
									No brand colors available
								</div>
							)}
						</div>
					</div>
				)}

				{isNestedFolder && (
					<div
						className="d-flex items-center mt-3 cursor-pointer brand-assets-back-button"
						onClick={() => {
							handleBackClick(breadCrumbsArray[breadCrumbsArray.length - 2]);
						}}
					>
						<BsArrowLeftShort className="me-1" size={22} />
						{t('back')}
					</div>
				)}

				<h4>{t('brandAssets')}</h4>
				{showBrandAssets ? (
					<div className="pt-3">
						<div className="custom-bay-breadcrumbs mb-3">
							<BayBreadcrumbs
								breadcrumbItemStyles={{ fontSize: '14px', fontWeight: '600' }}
								breadcrumbs={breadCrumbsArray}
								onSelect={handleBreadcrumbClick}
							/>
						</div>
						{/* <div className="mb-4">
							<SearchInput
								placeholder={t('commonSearchPlaceholder')}
								onChange={(e) => searchHandler(e)}
								value={brandSearchInput}
							/>
						</div> */}

						{isDesignAdmin && (
							<Button
								className="d-flex justify-content-center align-items-center btn btn-primary w-100 py-2"
								disabled={isLoading}
								variant="primary"
								onClick={() => {
									setShowUploadFileModal(true);
								}}
							>
								<BsPlusLg />
								<span className="ms-1">{t('addNewFile')}</span>
							</Button>
						)}

						{!isLoading ? (
							<>
								{assetFolders.map((folder) => {
									return (
										<div
											key={folder._id}
											className="d-flex align-items-center p-4 mt-4 rounded-2 fw-bold cursor-pointer"
											style={{
												backgroundColor: 'var(--bs-gray-700)',
												fontSize: 16,
											}}
											onClick={() => {
												handleRowClick(folder);
											}}
										>
											<div className="d-flex lh-1 align-items-center justify-content-start w-100 text-white">
												<span className="me-2 position-relative">
													<BsFolderFill size="1.5em" />
												</span>
												<BayTooltip overlay={folder.name}>
													<span className="text-truncate">{folder.name}</span>
												</BayTooltip>
												<BsChevronRight
													size="1.25em"
													style={{ marginLeft: 'auto' }}
												/>
											</div>
										</div>
									);
								})}

								{assetFiles && (
									<Masonry
										breakpointCols={{
											default: 2,
										}}
										className="masonry-grid mt-3"
										columnClassName="masonry_grid_column"
									>
										{assetFiles.map((file) => {
											return (
												<img
													key={file._id}
													alt={file.name}
													className="w-100 mb-3 rounded-2 cursor-pointer"
													crossOrigin="anonymous"
													src={`https://imageproxy.services.bkat.io/?url=${encodeURIComponent(file?.url ?? '')}&w=${151}`}
													onClick={() => handleImageSelect(file.url ?? '')}
												/>
											);
										})}
									</Masonry>
								)}
							</>
						) : (
							<BayWidgetLoadingPlaceholder height="100%" title="Loading..." />
						)}
					</div>
				) : (
					<p className="brand-no-assets">
						{t('noBrandAssets')}
					</p>
				)}

				{showUploadFileModal && (
					<BayFilesProgressModal
						{...(
							{
								allowedFileTypes: {
									'image/*': ['.png', '.jpg', '.jpeg'],
								},
								allowedFileTypesText: '.jpg, .jpeg, .png (15MB Limit)',
								apiConfig: {
									requestUrl: `${fileStoreApiUrl}/partitions/brand-assets/entries$File?onConflict=rename&path=${folderPathString.length > 0 ? folderPathString : ''}`,
									method: 'POST',
									withCredentials: true,
								},
								formDataObjectName: 'file',
								handleModalClose: (event: any) => {
									setShowUploadFileModal(event.show);
								},
								showUploadingFilesModal: showUploadFileModal,
								uploadingFiles: uploadingFiles,
							} as any
						)}
					/>
				)}
			</div>
		);
	},
);

export default BrandPanel;
